<template>
  <div>
    <member-selector
      class="no-print"
      :groups="groups"
      :selected-group.sync="selectedGroups"
    />

    <div
      v-if="loading"
      class="text-center pa-4"
    >
      <v-progress-circular
        indeterminate
        color="success"
      />
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="tableRows"
      item-key="id"
      :item-class="rowClass"
      dense
      disable-sort
      hide-default-footer
      disable-pagination
      class="man-hour-table elevation-1"
    >
      <template v-slot:item.team="{ item }">
        <span :class="{ 'font-weight-bold': item.kind !== 'group' }">{{ item.teamLabel }}</span>
      </template>

      <template v-slot:item.group="{ item }">
        <span :class="{ 'font-weight-bold': item.kind !== 'group' }">{{ item.groupLabel }}</span>
      </template>

      <template v-slot:item.employees="{ item }">
        <span>{{ item.employees | number('0') }}</span>
      </template>

      <template v-slot:item.workingDays="{ item }">
        <span>{{ item.workingDays | number('0.00') }}</span>
      </template>

      <template v-slot:item.expectedHrs="{ item }">
        <span>{{ item.expectedHrs | number('0.00') }}</span>
      </template>

      <template v-slot:item.actualHrs="{ item }">
        <span>{{ item.actualHrs | number('0.00') }}</span>
      </template>

      <template v-slot:item.absenceLeaveHrs="{ item }">
        <span>{{ item.absenceLeaveHrs | number('0.00') }}</span>
      </template>

      <template v-slot:item.absentPercent="{ item }">
        <span>{{ item.absentPercent | number('0.00') }}%</span>
      </template>

      <template v-slot:item.otAfterWork="{ item }">
        <span>{{ item.otAfterWork | number('0.00') }}</span>
      </template>

      <template v-slot:item.otRest="{ item }">
        <span>{{ item.otRest | number('0.00') }}</span>
      </template>

      <template v-slot:item.otNight="{ item }">
        <span>{{ item.otNight | number('0.00') }}</span>
      </template>

      <template v-slot:item.otHoliday="{ item }">
        <span>{{ item.otHoliday | number('0.00') }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import moment from 'moment'
  import { fetchManHourReport } from '@/helpers/tikApi'
  import MemberSelector from '@/views/dashboard/components/MemberSelector'
  import { exportManHourToExcel } from '@/helpers/exportManHour'

  export default {
    name: 'ManHourReport',
    components: { MemberSelector },
    data: () => ({
      report: null,
      loading: false,
      selectedGroups: [],
    }),
    computed: {
      ...mapState('records', ['view', 'currentDate', 'startDate', 'endDate']),
      ...mapGetters([
        'team', 'company', 'teamFull', 'groups', 'teams', 'groupsFull', 'groupLabel',
      ]),
      teamNameMap () {
        return (this.teams || []).reduce((acc, t) => {
          acc[t.code] = t.name
          return acc
        }, {})
      },
      groupMap () {
        return (this.groupsFull || []).reduce((acc, g) => {
          acc[g.code] = g.name
          return acc
        }, {})
      },
      headers () {
        return [
          { text: this.$t('teams'), value: 'team', width: '110px' },
          { text: this.groupLabel, value: 'group', width: '160px' },
          { text: 'No of Employees', value: 'employees', align: 'center' },
          { text: 'Total Working Days', value: 'workingDays', align: 'center' },
          { text: 'Expected working hrs', value: 'expectedHrs', align: 'center' },
          { text: 'Actual Working Hours', value: 'actualHrs', align: 'center' },
          { text: 'Absence/Leave hours', value: 'absenceLeaveHrs', align: 'center' },
          { text: 'Absent %', value: 'absentPercent', align: 'center' },
          { text: 'OT Hours After work', value: 'otAfterWork', align: 'center' },
          { text: 'OT Rest', value: 'otRest', align: 'center' },
          { text: 'OT Night', value: 'otNight', align: 'center' },
          { text: 'OT Holiday', value: 'otHoliday', align: 'center' },
        ]
      },
      tableRows () {
        const rows = []
        const teams = (this.report && this.report.teams) || []
        teams.forEach(t => {
          const teamLabel = this.teamNameMap[t.team] || t.team || ''
          ;(t.rows || []).forEach((r, i) => {
            rows.push(this.toRow(r, 'group', `${t.team}-${r.group || 'ungrouped'}`, i === 0 ? teamLabel : '', r.group))
          })
          if (t.rows && t.rows.length) {
            rows.push(this.toRow(t.subtotal, 'subtotal', `${t.team}-subtotal`, '', 'Subtotal'))
          }
        })
        if (this.report && this.report.total) {
          rows.push(this.toRow(this.report.total, 'total', 'grand-total', '', 'Total'))
        }
        return rows
      },
    },
    watch: {
      startDate () {
        this.loadReport()
      },
      endDate () {
        this.loadReport()
      },
      team () {
        this.loadReport()
      },
      selectedGroups () {
        this.loadReport()
      },
    },
    created () {
      this.loadReport()
    },
    mounted () {
      this.$root.$on('export-manhour-excel', this.exportToExcel)
    },
    beforeDestroy () {
      this.$root.$off('export-manhour-excel', this.exportToExcel)
    },
    methods: {
      toRow (r, kind, id, teamLabel, group) {
        return {
          id,
          kind,
          teamLabel,
          group: group || '',
          groupLabel: kind === 'group' ? (this.groupMap[group] || group || '') : group,
          employees: r.employees,
          workingDays: r.workingDays,
          expectedHrs: r.expectedHrs,
          actualHrs: r.actualHrs,
          absenceLeaveHrs: r.absenceLeaveHrs,
          absentPercent: r.absentPercent,
          otAfterWork: r.otAfterWork,
          otRest: r.otRest,
          otNight: r.otNight,
          otHoliday: r.otHoliday,
        }
      },
      rowClass (item) {
        if (item.kind === 'total') return 'total-row'
        if (item.kind === 'subtotal') return 'subtotal-row'
        return ''
      },
      async loadReport () {
        if (!this.startDate || !this.endDate) return
        this.loading = true
        try {
          const groups = this.selectedGroups.length ? this.selectedGroups.join(',') : null
          this.report = await fetchManHourReport(
            this.startDate,
            this.endDate,
            this.team,
            false,
            groups
          )
        } catch (e) {
          console.error('Failed to load man hour report', e)
        } finally {
          this.loading = false
        }
      },
      exportToExcel () {
        if (!this.report) return
        const sDate = moment(this.startDate).format('DD/MM/YYYY')
        const eDate = moment(this.endDate).format('DD/MM/YYYY')
        const companyHeader = `${this.company || ''} ${this.teamFull?.name || ''}`.trim()

        const withNames = (r) => ({ ...r, groupLabel: this.groupMap[r.group] || r.group || '' })
        const teams = (this.report.teams || []).map(t => ({
          team: this.teamNameMap[t.team] || t.team || '',
          rows: (t.rows || []).map(withNames),
          subtotal: t.subtotal,
        }))

        exportManHourToExcel({
          teams,
          total: this.report.total,
          companyHeader,
          title: 'Man Hour Report',
          dateRangeText: `Period: ${sDate} - ${eDate}`,
          groupLabel: this.groupLabel,
          filename: `Man Hour Report - ${sDate.replace(/\//g, '-')}`,
        })
      },
    },
  }
</script>

<style scoped>
.man-hour-table >>> .subtotal-row td {
  font-weight: bold;
  border-top: 2px solid rgba(128, 128, 128, 0.4);
}

.man-hour-table >>> .total-row td {
  font-weight: bold;
  border-top: 2px solid rgba(128, 128, 128, 0.6);
  border-bottom: 2px solid rgba(128, 128, 128, 0.6);
  background: rgba(0, 0, 0, 0.04);
}
</style>
